import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Card, Col, DatePicker, Input, message, Row, Select, Space, Statistic, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useState, useCallback } from 'react';
import axios from '../../api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const TokenStats = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [stats, setStats] = useState({
    total: 0,
    today: 0,
    yesterday: 0,
    week: 0,
    month: 0
  });
  const [dateRange, setDateRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [agents, setAgents] = useState([]);
  const [modelApis, setModelApis] = useState([]);
  const [filters, setFilters] = useState({
    agent: undefined,
    apikey: undefined,
    search: '',
    username: ''
  });

  // 获取智能体列表
  const fetchAgents = async () => {
    try {
      const res = await axios.get('/api/agents/', { params: { page_size: 1000 } });
      setAgents(normalizeList(res.data));
    } catch (err) {
      console.error('[TokenStats] 获取智能体列表失败:', err);
      setAgents([]);
    }
  };

  // 获取API列表
  const fetchModelApis = async () => {
    try {
      const res = await axios.get('/api/modelapis/');
      setModelApis(normalizeList(res.data));
    } catch (err) {
      console.error('[TokenStats] 获取API列表失败:', err);
      setModelApis([]);
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchModelApis();
  }, []);

  // 获取token消耗明细
  const fetchData = useCallback(async (page = 1, pageSize = 10) => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;

    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        start_date: dateRange[0].format('YYYY-MM-DD'),
        end_date: dateRange[1].format('YYYY-MM-DD'),
        ...filters,
      };
      console.log('[TokenStats] 开始获取token消耗明细');
      console.log('[TokenStats] 请求参数:', params);
      console.log('[TokenStats] 认证信息:', axios.defaults.headers.common);

      const res = await axios.get('/api/tokenusages/', { params });

      console.log('[TokenStats] 获取token消耗明细成功');
      console.log('[TokenStats] 响应状态:', res.status);
      console.log('[TokenStats] 响应数据:', res.data);
      console.log('[TokenStats] 响应头:', res.headers);

      setData(normalizeList(res.data));
      setPagination({
        current: page,
        pageSize,
        total: res.data?.count ?? normalizeList(res.data).length,
      });
    } catch (err) {
      console.error('[TokenStats] 获取token消耗明细失败');
      console.error('[TokenStats] 错误详情:', err);
      console.error('[TokenStats] 错误响应:', err.response?.data);
      console.error('[TokenStats] 错误状态:', err.response?.status);
      console.error('[TokenStats] 错误头:', err.response?.headers);
      setData([]);
      message.error('获取token消耗明细失败');
    } finally {
      setLoading(false);
    }
  }, [dateRange, filters]);

  // 获取统计数据
  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get('/api/tokenusages/stats/');
      setStats(res.data);
    } catch (err) {
      console.error('[TokenStats] 获取统计数据失败:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [fetchData, fetchStats]);

  const handleTableChange = useCallback((pagination) => {
    fetchData(pagination.current, pagination.pageSize);
  }, [fetchData]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSearch = useCallback((value, type) => {
    setFilters(prev => ({ ...prev, [type]: value }));
  }, []);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (text) => <b>{text}</b>,
    },
    {
      title: '智能体',
      dataIndex: 'agent_name',
      key: 'agent_name',
      width: 150,
    },
    {
      title: 'API-key',
      dataIndex: 'apikey',
      key: 'apikey',
      width: 200,
      render: (text) => text ? `${text.slice(0, 8)}...` : '-',
    },
    {
      title: '消耗token数',
      dataIndex: 'tokens',
      key: 'tokens',
      width: 120,
      sorter: (a, b) => a.tokens - b.tokens,
    },
    {
      title: '调用时间',
      dataIndex: 'created',
      key: 'created',
      width: 180,
      render: (text) => dayjs(text).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => dayjs(a.created).unix() - dayjs(b.created).unix(),
    },
    {
      title: '请求内容摘要',
      dataIndex: 'prompt',
      key: 'prompt',
      ellipsis: true,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card style={{ marginBottom: 4 }} styles={{ body: { padding: 4 } }}>
        <Space size="middle" style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space size="middle" wrap>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates?.[0] && dates?.[1]) setDateRange(dates);
              }}
              style={{ width: 280 }}
              size="small"
            />
            <Select
              placeholder="选择智能体"
              style={{ width: 160 }}
              allowClear
              value={filters.agent}
              onChange={(value) => handleFilterChange('agent', value)}
              size="small"
            >
              {agents.map(agent => (
                <Option key={agent.id} value={agent.id}>{agent.name}</Option>
              ))}
            </Select>
            <Select
              placeholder="选择API"
              style={{ width: 160 }}
              allowClear
              value={filters.apikey}
              onChange={(value) => handleFilterChange('apikey', value)}
              size="small"
            >
              {modelApis.map(api => (
                <Option key={api.id} value={api.apikey}>{api.model_display}</Option>
              ))}
            </Select>
            <Input.Search
              placeholder="搜索用户名"
              style={{ width: 160 }}
              onSearch={(value) => handleSearch(value, 'username')}
              allowClear
              size="small"
            />
            <Input.Search
              placeholder="搜索提示词"
              style={{ width: 160 }}
              onSearch={(value) => handleSearch(value, 'search')}
              allowClear
              size="small"
            />
          </Space>
        </Space>
      </Card>
      <Row gutter={[8, 8]} style={{ marginTop: 0, marginBottom: 0, paddingBottom: 0 }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 4 }}>
          <Card
            size="small"
            styles={{ body: { padding: 8, minHeight: 40 } }}
          >
            <Statistic
              title={<span style={{ fontSize: '12px', color: '#666' }}>总消耗</span>}
              value={stats.total}
              valueStyle={{ color: '#3f8600', fontSize: '18px', fontWeight: 'bold' }}
              prefix={<ArrowUpOutlined style={{ fontSize: '14px' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 4 }}>
          <Card
            size="small"
            styles={{ body: { padding: 8, minHeight: 40 } }}
          >
            <Statistic
              title={<span style={{ fontSize: '12px', color: '#666' }}>今日消耗</span>}
              value={stats.today}
              valueStyle={{ color: '#fa8c16', fontSize: '18px', fontWeight: 'bold' }}
              prefix={<ArrowUpOutlined style={{ fontSize: '14px' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 4 }}>
          <Card
            size="small"
            styles={{ body: { padding: 8, minHeight: 40 } }}
          >
            <Statistic
              title={<span style={{ fontSize: '12px', color: '#666' }}>昨日消耗</span>}
              value={stats.yesterday}
              valueStyle={{ color: '#52c41a', fontSize: '18px', fontWeight: 'bold' }}
              prefix={<ArrowUpOutlined style={{ fontSize: '14px' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 4 }}>
          <Card
            size="small"
            styles={{ body: { padding: 8, minHeight: 40 } }}
          >
            <Statistic
              title={<span style={{ fontSize: '12px', color: '#666' }}>本周消耗</span>}
              value={stats.week}
              valueStyle={{ color: '#1890ff', fontSize: '18px', fontWeight: 'bold' }}
              prefix={<ArrowUpOutlined style={{ fontSize: '14px' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 4 }}>
          <Card
            size="small"
            styles={{ body: { padding: 8, minHeight: 40 } }}
          >
            <Statistic
              title={<span style={{ fontSize: '12px', color: '#666' }}>本月消耗</span>}
              value={stats.month}
              valueStyle={{ color: '#eb2f96', fontSize: '18px', fontWeight: 'bold' }}
              prefix={<ArrowUpOutlined style={{ fontSize: '14px' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginBottom: 4 }}>
          <Card
            size="small"
            styles={{ body: { padding: 8, minHeight: 40 } }}
          >
            <Statistic
              title={<span style={{ fontSize: '12px', color: '#666' }}>平均消耗</span>}
              value={stats.total > 0 ? Math.round(stats.total / 30) : 0}
              valueStyle={{ color: '#722ed1', fontSize: '18px', fontWeight: 'bold' }}
              prefix={<ArrowDownOutlined style={{ fontSize: '14px' }} />}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 8 }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
        />
      </Card>
    </div>
  );
};

export default TokenStats;
